import { mapGetters, mapMutations } from 'vuex';
import cloneDeep from 'lodash.clonedeep';
import {
  CoverComposition,
  CoverTypeValue,
  PaymentFrequency,
  MyCircumstances,
  ProductType,
  LifetimeHealthCoverSituation,
  ProductTypeCodes,
  CoverType,
  DiscountsList,
  LoadingsList,
} from '@general/js/enums';
import { isPastDate, reverseFormatFormDate, firstLetterToUpperCase, formatFormDate, summaryCoverType } from '@general/js/utils';
import { convertCodeToValue } from '@/utils/CRMUtils';
import { VUEX_CHANGE_PRODUCT, VUEX_CHANGE_COVER_ROOT, VUEX_CAPTURE_ADDITIONAL_INFORMATION } from '@/constants/ChangeCover/ChangeCoverVuexConstants';
import { CAPTURE_ADDITIONAL_INFORMATION, CHANGE_MY_PRODUCT, PRODUCT_SELECTION, MY_DETAILS_MEMBER_SELECTION, REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { InsurableTypeEnum, HospitalSituation } from '@/enums';
import { findPolicyHolder, getPartnerDetailsMP } from '@/utils/MemberUtils';
import CommonMixins from './CommonMixins';

export default {
  mixins: [CommonMixins],
  data() {
    return {
      frequencyOptions: [
        { code: 1, value: PaymentFrequency.FORTNIGHTLY },
        { code: 2, value: PaymentFrequency.MONTHLY },
        { code: 3, value: PaymentFrequency.QUARTERLY },
        { code: 4, value: PaymentFrequency.SEMI_ANNUAL },
        { code: 5, value: PaymentFrequency.ANNUAL },
      ],
      genderOptions: [
        { code: 200000000, value: 'F' },
        { code: 200000001, value: 'M' },
      ],
      excessOptions: [
        { code: 1, value: '0' },
        { code: 2, value: '250' },
        { code: 3, value: '500' },
        { code: 4, value: '700' },
        { code: 5, value: '750' },
        { code: 6, value: '200' },
        { code: 7, value: '300' },
        { code: 8, value: '400' },
        { code: 9, value: '800' },
      ],
      MY_RECOMMENDATIONS_INFORMATION_TEXT:
        '<p>If you have discharged from fulltime service to a civilian and have notified us in the past 12 months you may be eligible for a transitional discount. If you are eligible, the discount will be applied at the confirmation step.</p>',
    };
  },

  updated() {
    this.bindRefDataLinks();
  },

  computed: {
    ...mapGetters('ChangeCoverRoot', {
      referenceData: VUEX_CHANGE_COVER_ROOT.GETTERS.getReferenceData,
      memberDetails: VUEX_CHANGE_COVER_ROOT.GETTERS.getMemberDetails,
      membershipPolicy: VUEX_CHANGE_COVER_ROOT.GETTERS.getMembershipPolicy,
      eligibilityDetails: VUEX_CHANGE_COVER_ROOT.GETTERS.getEligibilityDetails,
      membershipProducts: VUEX_CHANGE_COVER_ROOT.GETTERS.getMembershipProducts,
      currentComposition: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentComposition,
      currentRebateTier: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentRebateTier,
      currentMyCircumstances: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentMyCircumstances,
      currentExcess: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentExcess,
      currentNumberOfChildren: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentChildren,
      currentState: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentState,
      currentEligibility: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentEligibility,
      currentPremium: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentPremium,
      currentCoverStartDate: VUEX_CHANGE_COVER_ROOT.GETTERS.getCurrentCoverStartDate,
      policyHolderProductHistory: VUEX_CHANGE_PRODUCT.GETTERS.getPolicyHolderProductHistory,
      partnerProductHistory: VUEX_CHANGE_PRODUCT.GETTERS.getPartnerProductHistory,
      submitPolicyInProcess: VUEX_CHANGE_COVER_ROOT.GETTERS.getSubmitPolicyInProcess,
    }),

    ...mapGetters('ChangeCoverRoot/ChangeProduct', {
      changeCoverData: VUEX_CHANGE_PRODUCT.GETTERS.getChangeCover,
      myDetails: VUEX_CHANGE_PRODUCT.GETTERS.getMyDetails,
      myCover: VUEX_CHANGE_PRODUCT.GETTERS.getMyCover,
      myNeeds: VUEX_CHANGE_PRODUCT.GETTERS.getMyNeeds,
      closedHospitalProduct: VUEX_CHANGE_PRODUCT.GETTERS.getClosedHospitalProduct,
      closedExtrasProduct: VUEX_CHANGE_PRODUCT.GETTERS.getClosedExtrasProduct,
      myRecommendations: VUEX_CHANGE_PRODUCT.GETTERS.getRecommendedProducts,
      paymentFrequency: VUEX_CHANGE_PRODUCT.GETTERS.getPaymentFrequency,
      selectedProducts: VUEX_CHANGE_PRODUCT.GETTERS.getSelectedProducts,
      selectedExcess: VUEX_CHANGE_PRODUCT.GETTERS.getSelectedExcess,
      isFromEmailQuote: VUEX_CHANGE_PRODUCT.GETTERS.getIsFromEmailQuote,
    }),

    ...mapGetters('ChangeCoverRoot/CaptureAdditionalInformation', {
      policyHolderStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getPolicyHolderInformation,
      partnerStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getPartnerInformation,
      policyHolderInitialLhc: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPolicyHolderLhc,
      policyHolderInitialAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPolicyHolderAbd,
      partnerInitialLhc: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPartnerLhc,
      partnerInitialAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPartnerAbd,
      policyHolderInitialPortalAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPolicyHolderPortalAbd,
      partnerInitialPortalAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.GETTERS.getInitialPartnerPortalAbd,
    }),

    isPolicyHolderOrPartnerAContributor() {
      return this.isPolicyHolderAContributor || this.isPartnerAContributor;
    },

    isPolicyHolderAResponsibleAdult() {
      const activePolicyHolder = findPolicyHolder(this.memberDetails, false, true);
      return activePolicyHolder?.insurableType === InsurableTypeEnum.RESPONSIBLE;
    },

    isPolicyHolderAContributor() {
      const activePolicyHolder = findPolicyHolder(this.memberDetails, false, true);
      return activePolicyHolder?.insurableType === InsurableTypeEnum.CONTRIBUTOR;
    },

    isPolicyHolderAnInsuredAdult() {
      const activePolicyHolder = findPolicyHolder(this.memberDetails, false, true);
      return activePolicyHolder?.insurableType === InsurableTypeEnum.ADULT;
    },

    isPartnerAContributor() {
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.insurableType === InsurableTypeEnum.CONTRIBUTOR;
    },

    isPartnerAnInsuredAdult() {
      const partner = getPartnerDetailsMP(this.memberDetails);
      return partner?.insurableType === InsurableTypeEnum.ADULT;
    },

    coverCompositionArr() {
      const coverCompositionWithCodes = Object.values(CoverComposition).map((obj, index) => {
        const newObj = {
          code: index + 1,
          value: obj,
        };
        return newObj;
      });
      return coverCompositionWithCodes;
    },

    productTypeArr() {
      const productTypeWithCodes = Object.values(CoverTypeValue).map((obj, index) => {
        const newObj = {
          code: index + 1,
          value: obj,
        };
        return newObj;
      });
      return productTypeWithCodes;
    },

    /**
     * Reformats change cover data to add correct values needed for PriceFactorStatement
     */
    changeCoverEnteredData() {
      if (this.changeCoverData) {
        const changeCoverData = cloneDeep(this.changeCoverData);
        let incomeTierValues;
        if (this.changeCoverData.step2.composition === MyCircumstances.SINGLE && this.changeCoverData.step2.myCircumstances === MyCircumstances.SINGLE) {
          incomeTierValues = this.referenceData.incomeTier.options;
        } else {
          incomeTierValues = this.referenceData.familyIncomeTier.options;
        }

        const selectedIncomeTierValue = incomeTierValues.find((tierValue) => {
          return tierValue.returnValue === changeCoverData.step2.rebateTier;
        });
        changeCoverData.incomeTierOptions = incomeTierValues;
        if (selectedIncomeTierValue) {
          changeCoverData.rebateTierDisplay = selectedIncomeTierValue.display;
        }
        changeCoverData.step2.incomeTier = changeCoverData.step2.rebateTier;
        if (changeCoverData.step2.familyPlus) {
          if (changeCoverData.step2.composition === CoverComposition.FAMILY) {
            changeCoverData.step2.coverComposition = CoverComposition.FAMILY_PLUS;
          } else if (changeCoverData.step2.composition === CoverComposition.SINGLE_PARENT) {
            changeCoverData.step2.coverComposition = CoverComposition.SINGLE_PARENT_PLUS;
          } else {
            changeCoverData.step2.coverComposition = changeCoverData.step2.composition;
          }
        } else {
          changeCoverData.step2.coverComposition = changeCoverData.step2.composition;
        }
        changeCoverData.step2.partnerDOB = this.partnerDOB;

        // Prevent passing in conditions if cover type becomes invalid for selected needs
        const coverType = summaryCoverType(changeCoverData.step2.coverType);
        changeCoverData.step3.hospitalNeeds = [CoverType.HOSPITAL_ONLY, CoverType.PACKAGE].includes(coverType) ? changeCoverData.step3.specificTreatmentCategory : [];
        changeCoverData.step3.extrasNeeds = [CoverType.EXTRAS_ONLY, CoverType.PACKAGE].includes(coverType) ? changeCoverData.step3.specificTreatmentModality : [];
        return changeCoverData;
      }
      return null;
    },

    policyHolderHasOrHadHospital() {
      if (!this.policyHolderProductHistory) {
        return false;
      }

      // Return true if the member has at least one current or historic hospital or package in their product history
      return this.policyHolderProductHistory.some((policy) => {
        return policy.productHistory.productInfo.some((product) => {
          const productType = product.type.toLowerCase();
          return productType === CoverTypeValue.HOSPITAL_ONLY.toLowerCase() || productType === CoverTypeValue.HOSPITAL_AND_EXTRAS.toLowerCase();
        });
      });
    },

    partnerHasOrHadHospital() {
      if (!this.partnerProductHistory) {
        return false;
      }

      // Return true if the member has at least one current or historic hospital or package in their product history
      return this.partnerProductHistory.some((policy) => {
        return policy.productHistory.productInfo.some((product) => {
          const productType = product.type.toLowerCase();
          return productType === CoverTypeValue.HOSPITAL_ONLY.toLowerCase() || productType === CoverTypeValue.HOSPITAL_AND_EXTRAS.toLowerCase();
        });
      });
    },

    currentCoverLevel() {
      if (this.membershipProducts.length === 2) {
        return CoverTypeValue.HOSPITAL_AND_EXTRAS;
      }
      if (this.membershipProducts.length === 1) {
        if (this.membershipProducts[0].productTypeCode === ProductTypeCodes.PACKAGE) {
          return CoverTypeValue.HOSPITAL_AND_EXTRAS;
        }
        return this.membershipProducts[0].productTypeCode === ProductTypeCodes.HOSPITAL_ONLY ? CoverTypeValue.HOSPITAL_ONLY : CoverTypeValue.EXTRAS_ONLY;
      }
      return null;
    },

    policyHolderState() {
      const wantsHospitalCover =
        this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS || this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_ONLY;
      const currentHospitalCover = this.currentCoverLevel !== CoverTypeValue.EXTRAS_ONLY;
      if (this.policyHolderHasOrHadHospital && currentHospitalCover && wantsHospitalCover) {
        return HospitalSituation.RETAINING;
      }
      if (!this.policyHolderHasOrHadHospital && !currentHospitalCover && wantsHospitalCover) {
        return HospitalSituation.APPLYING;
      }
      if (this.policyHolderHasOrHadHospital && !currentHospitalCover && wantsHospitalCover) {
        return HospitalSituation.REAPPLYING;
      }
      return null;
    },

    partnerHospitalState() {
      const wantsHospitalCover =
        this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS || this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_ONLY;
      const currentHospitalCover = this.currentCoverLevel !== CoverTypeValue.EXTRAS_ONLY;

      if (!wantsHospitalCover) {
        return null;
      }
      // New partner will always be applying
      if (!this.changeCoverData?.step2?.partner?.keepAdult) {
        return HospitalSituation.APPLYING;
      }

      if (this.partnerHasOrHadHospital && currentHospitalCover) {
        return HospitalSituation.RETAINING;
      }
      if (!this.partnerHasOrHadHospital && !currentHospitalCover) {
        return HospitalSituation.APPLYING;
      }
      if (this.partnerHasOrHadHospital && !currentHospitalCover) {
        return HospitalSituation.REAPPLYING;
      }

      return null;
    },

    currentMembershipHasPartner() {
      const partner = getPartnerDetailsMP(this.memberDetails, true);
      return partner !== null && partner !== undefined;
    },

    selectedCompositionSupportsPartner() {
      if (this.changeCoverData.step2.composition !== null) {
        return [CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS].includes(this.changeCoverData.step2.composition);
      }
      return false;
    },

    showMyCoverNewPartnerSection() {
      return (!this.changeCoverData.step2?.partner?.keepAdult || !this.currentMembershipHasPartner) && this.selectedCompositionSupportsPartner;
    },

    partnerDOB() {
      if (!this.showMyCoverNewPartnerSection) {
        return this.changeCoverData?.step2?.partner?.dob ?? null;
      }
      return this.changeCoverData?.step2?.partner?.newPartner?.DOB ?? null;
    },
  },

  methods: {
    ...mapMutations('ChangeCoverRoot', {
      updateReferenceData: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateReferenceData,
      updateMemberDetails: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMemberDetails,
      updateMembershipProducts: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMembershipProducts,
      updateMembershipPolicy: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateMembershipPolicy,
      updateEligibilityDetails: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateEligibilityDetails,
      updateCoverComposition: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentComposition,
      updateCurrentRebateTier: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentRebateTier,
      updateCurrentMyCircumstances: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentMyCircumstances,
      updateCurrentExcess: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentExcess,
      updateCurrentNumberOfChildren: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentChildren,
      updateCurrentState: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentState,
      updateCurrentEligibility: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentEligibility,
      updateCurrentPremium: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateCurrentPremium,
      updatePolicyHolderProductHistory: VUEX_CHANGE_PRODUCT.MUTATIONS.updatePolicyHolderProductHistory,
      updatePartnerProductHistory: VUEX_CHANGE_PRODUCT.MUTATIONS.updatePartnerProductHistory,
      updateSubmitPolicyInProcess: VUEX_CHANGE_COVER_ROOT.MUTATIONS.updateSubmitPolicyInProcess,
    }),

    ...mapMutations('ChangeCoverRoot/ChangeProduct', {
      updateMyDetails: VUEX_CHANGE_PRODUCT.MUTATIONS.updateMyDetails,
      updateMyCover: VUEX_CHANGE_PRODUCT.MUTATIONS.updateMyCover,
      updateMyNeeds: VUEX_CHANGE_PRODUCT.MUTATIONS.updateMyNeeds,
      updateClosedHospitalProduct: VUEX_CHANGE_PRODUCT.MUTATIONS.updateClosedHospitalProduct,
      updateClosedExtrasProduct: VUEX_CHANGE_PRODUCT.MUTATIONS.updateClosedExtrasProduct,
      updateMyRecommendations: VUEX_CHANGE_PRODUCT.MUTATIONS.updateRecommendedProducts,
      updatePaymentFrequency: VUEX_CHANGE_PRODUCT.MUTATIONS.updatePaymentFrequency,
      resetMyNeeds: VUEX_CHANGE_PRODUCT.MUTATIONS.resetMyNeeds,
      updateSelectedProducts: VUEX_CHANGE_PRODUCT.MUTATIONS.updateSelectedProducts,
      updateSelectedExcess: VUEX_CHANGE_PRODUCT.MUTATIONS.updateSelectedExcess,
      resetChangeCoverData: VUEX_CHANGE_PRODUCT.MUTATIONS.resetChangeCoverData,
      updateIsFromEmailQuote: VUEX_CHANGE_PRODUCT.MUTATIONS.updateIsFromEmailQuote,
    }),

    ...mapMutations('ChangeCoverRoot/CaptureAdditionalInformation', {
      updatePolicyHolderStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updatePolicyHolder,
      updatePartnerStep: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updatePartner,
      updateInitialPolicyHolderLhc: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPolicyHolderLhc,
      updateInitialPolicyHolderAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPolicyHolderAbd,
      updateInitialPartnerLhc: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPartnerLhc,
      updateInitialPartnerAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPartnerAbd,
      updateInitialPolicyHolderPortalAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPolicyHolderPortalAbd,
      updateInitialPartnerPortalAbd: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPartnerPortalAbd,
      updateInitialPolicyHolderVeteranCard: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPolicyHolderVeteranCard,
      updateInitialPartnerVeteranCard: VUEX_CAPTURE_ADDITIONAL_INFORMATION.MUTATIONS.updateInitialPartnerVeteranCard,
    }),

    /**
     * Get the cover type for closed product
     * This flow will only occur if the member has selected Hospital and Extras as the cover type.
     * - If member currently has both closed and open product, send the reviewCover (since the question will determine what will be reviewed)
     * - If member is currently on closed hospital product, send for 'both' if 'hospital' is selected (in review question), send 'extras' if 'extras' is selected
     * - If member is currently on closed extra product, send 'both' if 'extras' is selected (in review question), send 'both; if 'hospital' is selected
     * @returns The product that needs to be reviewed (sent out to api) or null
     */
    getCoverTypeForClosedProduct() {
      const reviewCover = this.changeCoverData.step2.reviewCover;
      if (this.closedHospitalProduct && this.closedExtrasProduct) {
        return reviewCover;
      }
      if (this.closedHospitalProduct) {
        if (reviewCover === CoverTypeValue.HOSPITAL_ONLY) {
          return CoverTypeValue.HOSPITAL_AND_EXTRAS;
        }
        if (reviewCover === CoverTypeValue.EXTRAS_ONLY) {
          return CoverTypeValue.EXTRAS_ONLY;
        }
      }
      if (this.closedExtrasProduct) {
        if (reviewCover === CoverTypeValue.HOSPITAL_ONLY) {
          return CoverTypeValue.HOSPITAL_ONLY;
        }
        if (reviewCover === CoverTypeValue.EXTRAS_ONLY) {
          return CoverTypeValue.HOSPITAL_AND_EXTRAS;
        }
      }
      return null;
    },

    updatePaymentFrequencyIntoVuex() {
      this.updatePaymentFrequency(firstLetterToUpperCase(this.membershipPolicy.frequency));
    },

    updateCoverCompositionIntoVuex() {
      const coverComposition = this.membershipPolicy.composition;
      this.updateCoverComposition(coverComposition);
    },

    updateCurrentExcessIntoVuex() {
      this.updateCurrentExcess(this.membershipPolicy.excess);
    },

    getClosedHospitalProduct() {
      if (this.membershipProducts) {
        // Hospital Product
        // Get end product date
        const hospitalProduct = this.membershipProducts.find((el) => {
          return el.productTypeCode === this.productTypeArr[0].code; // Hospital = 1
        });

        if (hospitalProduct) {
          const hospitalEndDate = hospitalProduct.validToDate;
          const productExcess = hospitalProduct.productExcess.find((el) => {
            const excessComposition = el.composition.split(',').map((compositionKey) => {
              const coverComposition = convertCodeToValue(this.coverCompositionArr, compositionKey);
              return coverComposition;
            });
            return el.excess.toString() === this.currentExcess && excessComposition.includes(this.coverComposition);
          });

          let closedHospital = null;
          if (hospitalEndDate && productExcess) {
            // Check whether hospital product is closed or not depending on end date
            const productStatus = isPastDate(reverseFormatFormDate(hospitalEndDate));

            // Check also excess end date
            const excessStatus = isPastDate(reverseFormatFormDate(productExcess.endDate));

            closedHospital = productStatus && excessStatus ? hospitalProduct : null;
          } else if (hospitalEndDate) {
            // Check whether hospital product is closed or not depending on end date
            const productStatus = isPastDate(reverseFormatFormDate(hospitalEndDate));

            closedHospital = productStatus ? hospitalProduct : null;
          }

          this.updateClosedHospitalProduct(closedHospital);
        }
      }
    },

    getClosedExtrasProduct() {
      if (this.membershipProducts) {
        // Extras Product
        const extrasProduct = this.membershipProducts.find((el) => {
          return el.productTypeCode === this.productTypeArr[1].code; // Extras = 2
        });

        if (extrasProduct && extrasProduct.validToDate && isPastDate(reverseFormatFormDate(extrasProduct.validToDate))) {
          const closedExtras = extrasProduct;
          this.updateClosedExtrasProduct(closedExtras);
        }
      }
    },

    getPaidToDate() {
      return this.membershipPolicy.datePaidTo;
    },

    redirectToCaptureInfoPage() {
      this.$router.push({ name: CAPTURE_ADDITIONAL_INFORMATION });
    },

    redirectToQAPage() {
      this.$router.push({ name: PRODUCT_SELECTION });
    },

    redirectToChangeProductPage() {
      this.$router.push({ name: CHANGE_MY_PRODUCT });
    },

    redirectToChangeCoverStep(targetPageName, fromCoverSelectionPage = true) {
      setNavigationState({
        stepName: targetPageName,
        fromCoverSelectionPage,
      });
      this.$router
        .push({
          name: CHANGE_MY_PRODUCT,
        })
        .catch((error) => {
          if (error.name !== 'NavigationDuplicated') throw error;
        });
    },

    setHover(event) {
      if (event.target.nodeName === 'SPAN') {
        event.target.parentElement.parentElement.previousElementSibling.classList.add('hover');
      } else {
        event.target.parentElement.previousElementSibling.classList.add('hover');
      }
    },

    removeHover(event) {
      if (event.target.nodeName === 'SPAN') {
        event.target.parentElement.parentElement.previousElementSibling.classList.remove('hover');
      } else {
        event.target.parentElement.previousElementSibling.classList.remove('hover');
      }
    },

    hasHospitalFeatures(recommendation) {
      return recommendation.coverType === CoverTypeValue.HOSPITAL_ONLY || recommendation.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS;
    },

    hasExtrasFeatures(recommendation) {
      return recommendation.coverType === CoverTypeValue.EXTRAS_ONLY || recommendation.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS;
    },

    getFeature(fullRecommendationList, type, featureName, recommendationIndex, isFlexi = false) {
      let val = 'Excluded';

      fullRecommendationList[recommendationIndex].products.forEach((product) => {
        if (product.productType.toLowerCase() === type.toLowerCase()) {
          const matchingFeature = product.productProperties.find((feature) => {
            return feature.propertyName.toLowerCase() === featureName.toLowerCase() && feature.flexiLimit === isFlexi;
          });

          if (matchingFeature) {
            val = matchingFeature.propertyValue;
          }
        } else if (product.productType.toLowerCase() === ProductType.PACKAGE) {
          product.productProperties.forEach((feature) => {
            if (feature.propertyType.toLowerCase() === type.toLowerCase() && feature.propertyName.toLowerCase() === featureName.toLowerCase() && feature.flexiLimit === isFlexi) {
              val = feature.propertyValue;
            }
          });
        }
      });

      return val;
    },

    bindRefDataLinks() {
      const CHANGE_PRODUCT_SELECTORS = [
        {
          id: '#myDetails',
          stepName: 'MyDetails',
        },
        {
          id: '#myCover',
          stepName: 'MyCover',
        },
        {
          id: '#myNeeds',
          stepName: 'MyNeeds',
        },
        {
          id: '#myRecommendations',
          stepName: 'MyRecommendations',
        },
      ];

      const PORTAL_SELECTORS = [
        {
          id: '#memberDetails',
          name: MY_DETAILS_MEMBER_SELECTION,
        },
        {
          id: '#rebates',
          name: REBATES_MEMBER_PORTAL.REBATE_OVERVIEW,
        },
      ];

      CHANGE_PRODUCT_SELECTORS.forEach((selector) => {
        if (document.querySelectorAll(selector.id).length > 0) {
          const elements = document.querySelectorAll(selector.id);

          elements.forEach((e) => {
            e.addEventListener('click', (event) => {
              event.preventDefault();
              this.redirectToChangeCoverStep(selector.stepName);
            });
          });
        }
      });

      PORTAL_SELECTORS.forEach((selector) => {
        if (document.querySelectorAll(selector.id).length > 0) {
          const elements = document.querySelectorAll(selector.id);

          elements.forEach((e) => {
            e.addEventListener('click', (event) => {
              event.preventDefault();
              return this.$router.push({
                name: selector.name,
              });
            });
          });
        }
      });
    },

    formatLHCDetails(memberInfo) {
      if (memberInfo.lhc) {
        const lhc = {
          cae: memberInfo.lhc.cae ?? null,
          overruledReason: memberInfo.lhc.overruledReason ?? LifetimeHealthCoverSituation.NONE,
        };

        const overruledReason = lhc.overruledReason;
        if (overruledReason === LifetimeHealthCoverSituation.EX_ADF) {
          // ADF overrule has two dates
          lhc.date = formatFormDate(memberInfo.lhc.date);
          lhc.dischargeDate = formatFormDate(memberInfo.lhc.dischargeDate);
        } else {
          lhc.date = memberInfo.date ? formatFormDate(memberInfo.date) : null;
        }
        return lhc;
      }
      return null;
    },

    compositionIncludesChildren(composition) {
      return [CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT, CoverComposition.CHILDREN_ONLY, CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(
        composition,
      );
    },

    getDiscountsAndLoadingsList(discountsAndLoadingsObj) {
      if (!discountsAndLoadingsObj) {
        return {};
      }

      const adjustments = { discounts: [], loadings: [] };

      Object.entries(discountsAndLoadingsObj)
        .map(([key, value]) => {
          return { category: key, percentage: value };
        })
        .filter((adjustment) => {
          return adjustment.percentage !== null && adjustment.percentage !== '';
        })
        .forEach((adjustment) => {
          if (DiscountsList.includes(adjustment.category)) {
            adjustments.discounts.push(adjustment);
          } else if (LoadingsList.includes(adjustment.category)) {
            adjustments.loadings.push(adjustment);
          }
        });
      return adjustments;
    },

    convertCodeToValue,
  },
};
